import { useCallback, useEffect, useState } from 'react';
import { getCoursesAdmin } from '../api/courses';

// GET /courses/admin refuses a limit above 100 (PaginationDto @Max(100)), so a list that needs
// every course reads the pages one by one instead of asking for a bigger one.
const PAGE_SIZE = 100;
const MAX_PAGES = 20; // 2,000 courses: a stop for a backend that ignores `page`

/**
 * Every course GET /courses/admin returns for the caller, all pages.
 * For pickers and counters that have no "load more".
 * @param {object} [params] extra query params (search, isPublished, sortBy, sortOrder, ...)
 * @returns {Promise<Array>}
 */
export async function fetchAllCourses(params = {}) {
  const courses = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await getCoursesAdmin({ ...params, limit: PAGE_SIZE, page });
    const rows = Array.isArray(res) ? res : res?.data || [];
    courses.push(...rows);
    const totalPages = res?.meta?.totalPages;
    if (rows.length < PAGE_SIZE || (typeof totalPages === 'number' && page >= totalPages)) break;
  }
  return courses;
}

/**
 * Loads every course once (see fetchAllCourses) and keeps the failure, so the page can say that
 * the list did not load instead of showing an empty picker.
 * @param {{ enabled?: boolean }} [options] enabled=false waits (for example for the signed-in user)
 * @returns {{ courses: Array, loading: boolean, error: any, reload: function }}
 */
export function useAllCourses({ enabled = true } = {}) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAllCourses()
      .then((list) => { if (!cancelled) setCourses(list); })
      .catch((err) => { if (!cancelled) setError(err || new Error('Failed to load courses')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return { courses, loading, error, reload };
}
